<template>
  <div>
    <a-dialog
      v-model="show"
      width="900px"
      max-width="90vw"
      persistent
      scrollable
    >
      <v-card>
        <v-banner color="primaryLight1" sticky single-line tile>
          <v-toolbar color="primaryLight1">
            <span>{{ $t("template.lblEmailNotificationTemplate") }}</span>
            <v-spacer></v-spacer>
            <CloseIcon @click="close()" />
          </v-toolbar>
        </v-banner>
        <v-card-text>
          <v-tabs
            class="a-tabs role-tabs"
            show-arrows
            height="38"
            style="background-color: var(--v-Neutral-base)"
            right
            v-model="tabIndex"
            @change="onTabChange"
          >
            <v-tabs-slider color="primary"></v-tabs-slider>
            <v-tab class="ml-2">
              {{ $t("template.lblWrite") }}
            </v-tab>
            <v-tab> {{ $t("template.lblPreview") }} </v-tab>
          </v-tabs>
          <v-row class="pa-2">
            <v-col cols="12" sm="12" md="12">
              <v-tabs-items v-model="currentTab">
                <v-tab-item>
                  <v-form
                    ref="refEmailTemplateForm"
                    v-model="valid"
                    lazy-validation
                  >
                    <v-text-field
                      class="textField"
                      :label="$t('agenda.lblEmailSubject')"
                      v-model="item.subject"
                      required
                      filled
                      dense
                      color="neutralGray"
                      :rules="[
                        (v) => validateTemplate(v),
                         (v) => isUniqueSubject(v) || $t('agenda.msgSubjectUnique')
                        ]"
                        :maxlength="maxLength"
                    ></v-text-field>
                    <a-rich-text-editor
                      :label="$t('agenda.lblMessage')"
                      v-model="item.message"
                      class="mt-5 mb-1"
                      @descriptionChange="descriptionChanges"
                    ></a-rich-text-editor>
                    <span
                      v-if="isBodyRequired"
                      style="color: var(--v-error-base) !important"
                      >{{ $t("template.lblEmailBodyReq") }}</span
                    >
                    <span
                      v-if="!isTemplateBodyValid"
                      style="color: var(--v-error-base) !important"
                      >{{ $t("template.lblBodyPlaceholderErrors") }}</span
                    >
                  </v-form>
                </v-tab-item>
                <v-tab-item>
                  <template-preview :item="item"></template-preview>
                </v-tab-item>
              </v-tabs-items>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <placeholders
            :type="getTemplateType"
            :templateTypeId="item.templateTypeId"
          ></placeholders>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <a-btn
                class="mr-2"
                color="primaryDark"
                small
                v-bind="attrs"
                v-on="on"
                :disabled="isDescriptionWithImage || !isDirty"
                @click="onTemplateSave()"
                ><span>{{ $t("btnSave") }}</span>
              </a-btn>
            </template>
            <span> {{ $t("btnSave") }} </span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <a-btn
                color="secondary"
                small
                @click="close()"
                v-bind="attrs"
                v-on="on"
                ><span>{{ $t("btnCancel") }}</span>
              </a-btn>
            </template>
            <span> {{ $t("btnCancel") }} </span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </a-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { createUUID } from "../../utils/util";
import Placeholders from "./Placeholders.vue";
import TemplatePreview from "./TemplatePreview.vue";
import { cloneDeep } from "lodash";
import { TITLE_LENGTH_1000 } from "@/utils/constant";

export default {
  components: { Placeholders, TemplatePreview },
  name: "EmailTemplateEditor",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    templateItems: {
      type: Array,
      default: () => {
        return [];
      }
    },
    item: Object,
  },
  data() {
    return {
      isDescriptionWithImage: false,
      isTemplateValid: true,
      tabIndex: 0,
      currentTab: 0,
      isBodyRequired: false,
      isTemplateBodyValid: true,
      valid: true,
      showTextEditor: false,
      refItem: null,
      maxLength: TITLE_LENGTH_1000,
    };
  },
  computed: {
    ...mapGetters({
      masterSettingConfig: "tenant/masterSettingConfig",
      tenantSettingConfig: "tenant/tenantSettingConfig",
    }),
    isDirty() {
      if (this.item?.is_copy) return true;
      return JSON.stringify(this.refItem) !== JSON.stringify(this.item);
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value || false);
      },
    },
    templatePlaceHolderItems() {
      const templateVariablesObject = this.masterSettingConfig(
        "email_template_dynamic_variables"
      );
      return templateVariablesObject["email_template_dynamic_variables"];
    },
    replacedSubject: {
      get() {
        const replacedText = this.templatePlaceHolderItems.reduce(
          (result, item) => {
            return result.replace(new RegExp(item.value, "g"), item.text);
          },
          this.item.subject || ""
        );
        return replacedText;
      },
    },
    replacedMessage: {
      get() {
        const replacedText = this.templatePlaceHolderItems.reduce(
          (result, item) => {
            return result.replace(new RegExp(item.value, "g"), item.text);
          },
          this.item.message || ""
        );
        return replacedText;
      },
    },
    getTemplateType() {
      const typeMapping = {
        ConvocationMail: "meeting",
        RemindVotingConsent: "meeting",
        MeetingUpdate: "meeting",
        MeetingDeletion: "meeting",
        MeetingCreate: "meeting",
        MOMReviewRequest: "meeting",
        Task: "task",
        Chats: "chat",
        Survey: "survey",
      };
      return typeMapping[this.item.templateTypeId] || null;
    },
  },
  mounted() {
    this.refItem = cloneDeep(this.item);
    const thisInstance = this;
    setTimeout(() => {
      thisInstance.showTextEditor = true;
    }, 100);
  },
  methods: {
    ...mapActions({
      updateEmailTemplate: "tenant/updateEmailTemplate",
      createTenantConfig: "tenant/createTenantConfig",
    }),
    close,
    copyToClipboard,
    onTemplateSave,
    isEmailBodyValid,
    resetForm,
    onTabChange,
    isValidPlaceholder,
    checkValidateTemplate,
    descriptionChanges,
    validateTemplate,
    emitSuccess,
    isUniqueSubject(value) {
      // Check uniqueness ignoring the current selected.id
      return !this.templateItems.some(
        t => t.subject.trim().toLowerCase() === value.trim().toLowerCase()
          && t.id !== this.item.id
      );
    }
  },
};

function validateTemplate(value) {
  const stringValue = value.toString().trim();
  const placeholder = "{{%meeting_description%}}";

  if (!stringValue) {
    return this.$t("template.lblSubjectReq");
  }

  if (stringValue.includes(placeholder)) {
    this.isTemplateValid = false;
    return this.$t("template.lblTemplateErrors");
  }

  const isValid = this.checkValidateTemplate(stringValue);
  if (!isValid) {
    this.isTemplateValid = false;
    return this.$t("template.lblTemplateErrors");
  }

  this.isTemplateValid = true;
  return true;
}

function descriptionChanges(val) {
  this.isDescriptionWithImage = val;
}

function onTabChange() {
  this.currentTab = this.tabIndex;
}

function findPlaceholders(str, pattern) {
  const regex = new RegExp(pattern, "g");
  const matches = [];
  let match;

  while ((match = regex.exec(str)) !== null) {
    matches.push(match[0]);
  }
  return matches;
}

function findUnclosedPlaceholders(str) {
  return findPlaceholders(str, /{{%[\w_]+/g); // Matches placeholders starting with "{{%" but not closing
}

function findClosedPlaceholders(str) {
  return findPlaceholders(str, /{{%([^%]+)%}}/g); // Matches complete placeholders with "{{%" and "%}}"
}

function checkValidateTemplate(template) {
  const unclosedPlaceholders = findUnclosedPlaceholders(template).map(
    (item) => item.replace("{{%", "") + "%}}"
  );
  const closedPlaceholders = findPlaceholders(template, /\w+%}}/g);

  const unmatchedValues = unclosedPlaceholders.filter(
    (item) => !closedPlaceholders.includes(item)
  );

  if (unmatchedValues.length === 0) {
    const exactMatchPlaceholders = findClosedPlaceholders(template);
    return this.isValidPlaceholder(exactMatchPlaceholders);
  }
  return false;
}

function isValidPlaceholder(placeholders) {
  const isConvocationTemplate = this.item.templateTypeId === "ConvocationMail";
  const isMeetingCreateTemplate = this.item.templateTypeId === "MeetingCreate";

  let templatePlaceHolders = isConvocationTemplate
    ? this.templatePlaceHolderItems
    : this.templatePlaceHolderItems.filter(
        (f) => f.key !== "lblMeetingDescription"
      );

  if (isMeetingCreateTemplate) {
    templatePlaceHolders = templatePlaceHolders.filter(
      (p) => p.key !== "lblTopicDetails"
    );
  }

  const values = templatePlaceHolders
    .filter((f) => f.type === "general" || f.type === this.getTemplateType)
    .map((item) => item.value);

  return placeholders.every((placeholder) =>
    values.some((value) => value.includes(placeholder))
  );
}

async function onTemplateSave() {
  let loader;
  try {
    const isFormValid = this.$refs.refEmailTemplateForm.validate();
    const isBodyValid = this.isEmailBodyValid();

    if (!isFormValid || !isBodyValid) return;

    this.isTemplateValid = true;
    loader = this.$localLoader.show();

    const emailTemplates = this.tenantSettingConfig("email_templates");
    if (!emailTemplates) {
      const newId = createUUID();
      const templateObj = {
        id: createUUID(),
        tenantid: this.selectedTenant,
        configname: "email_templates",
        email_templates: {
          [this.item.templateTypeId]: {
            [newId]: {
              id: newId,
              subject: this.item.subject,
              body: this.item.message,
              active: false,
            },
          },
        },
      };
      await this.createTenantConfig(templateObj);
    } else {
      const templateData = {
        configname: "email_templates",
        template_type_id: this.item.templateTypeId,
        email_template: {
          id: this.item.id || createUUID(),
          subject: this.item.subject,
          body: this.item.message,
          active: this.item.active,
        },
      };

      await this.updateEmailTemplate(templateData);
    }

    this.emitSuccess(true);
    this.$localLoader.hide(loader);
  } catch (error) {
    this.emitSuccess(false);
    this.$localLoader.hide(loader);
  }
}

function emitSuccess(isSuccess) {
  this.$emit("success", {
    isSuccess,
    isUpdate: !!this.item.id,
  });
  this.close();
}

function isEmailBodyValid() {
  this.isBodyRequired = this.item.message === "";
  if (this.isBodyRequired) return false;

  this.isTemplateBodyValid = this.checkValidateTemplate(this.item.message);
  return this.isTemplateBodyValid;
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
}

function close() {
  this.$emit("input", false);
  this.resetForm();
}

function resetForm() {
  this.isBodyRequired = false;
  this.$refs.refEmailTemplateForm.reset();
  this.$refs.refEmailTemplateForm.resetValidation();
}
</script>

<style>
</style>